'use client';

import Navbar from '@/components/common/Navbar';
import Sidebar from '@/components/common/Sidebar';
import { AttendanceFilterProvider } from '@/context/AttendanceFilterContext';
import { EntityProvider } from '@/context/EntityContext';
import { PermissionProvider } from '@/context/PermissionContext';
import axiosInstance from '@/services/api/index';
import {
	getEntityBySecret,
	getHardcodeList,
} from '@/services/encrypt/entitySecretService';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { Button, Modal } from 'flowbite-react';
import 'leaflet/dist/leaflet.css';

import Head from 'next/head';
import { usePathname, useRouter } from 'next/navigation';
import { useEffect, useState } from 'react';
import { I18nextProvider } from 'react-i18next';
import i18n from '../i18n';
import './globals.css';


const queryClient = new QueryClient();

export default function RootLayout({
	children,
}: Readonly<{
	children: React.ReactNode;
}>) {
	useEffect(() => {
		const checkWebStatus = async () => {
			try {
				const currentHost =
					window.location.hostname +
					(window.location.port ? ':' + window.location.port : '');
				const response = await axiosInstance.get(
					'/general-parameter/hardcode/list-dynamic',
					{
						headers: {
							gh_hardcode: 'WEBSTATUS',
							gh_config_name: currentHost,
						},
					}
				);

				if (response.data.success && response.data.data.length > 0) {
					const configData = response.data.data[0];
					if (
						configData.gh_config_data &&
						configData.gh_config_data !== currentHost
					) {
						// Hanya gunakan protokol dan gh_config_data tanpa path tambahan
						const protocol = window.location.protocol;
						const targetUrl = `${protocol}//${configData.gh_config_data}`;
						window.location.href = targetUrl;
					}
				}
			} catch (error) {
				console.error('Error checking web status:', error);
			}
		};

		checkWebStatus();
	}, []);

	useEffect(() => {
		// Inisialisasi bahasa dari localStorage saat aplikasi dimuat
		const savedLanguage = localStorage.getItem('language') || 'id';
		i18n.changeLanguage(savedLanguage);
	}, []);
	const [isSidebarOpen, setIsSidebarOpen] = useState(false);
	const pathname = usePathname();

	const [languageProps, setLanguageProps] = useState(
		typeof window !== 'undefined' ? localStorage.getItem('title') || 'id' : 'id'
	);
	const [styleTheme, setStyleTheme] = useState('');
	const [pageTitle, setPageTitle] = useState('Hi-Guard');

	const toggleSidebar = () => {
		setIsSidebarOpen(!isSidebarOpen);
	};

	const handleChangeLanguage = (language: string) => {
		localStorage.setItem('title', language);
		setLanguageProps(language);
	};

	// Cek apakah rute saat ini adalah halaman login
	const isLoginPage = pathname === '/' || pathname === '/login';
	const isPrivacyPolicyPage = pathname === '/privacy-policy';
	const isChangePasswordPage = pathname === '/auth/change-password';
	const isNotFoundPage = pathname === '/not-found';

	const [showSessionExpiredModal, setShowSessionExpiredModal] = useState(false);
	const router = useRouter();

	// Check jika halaman loketss
	const splitPathName =
		pathname.split('/') && pathname.split('/').length > 1
			? pathname.split('/')[1]
			: '';
	const arrDisplayLocket = [
		'qo-queuelocket',
		'qo-display',
		'qo-kiosk',
		'loket',
	];
	const isLocketLayout = arrDisplayLocket.includes(splitPathName || '');
	const [faviconUrl, setFaviconUrl] = useState<string>('');
	// console.log({ splitPathName, isLocketLayout })

	useEffect(() => {
		const fetchEntityData = async () => {
			const timestamp = new Date().toISOString();
			console.log(`[Layout][${timestamp}] Memulai fetch entity data`);

			try {
				const currentHost = window.location.hostname;
				console.log(`[Layout][${timestamp}] Current hostname:`, currentHost);

				const hardcodeResponse = await getHardcodeList();
				console.log(`[Layout][${timestamp}] Hardcode response:`, {
					success: hardcodeResponse.success,
					totalConfigs: hardcodeResponse.data.length,
				});

				const matchingConfig = hardcodeResponse.data.find((config) =>
					currentHost.includes(config.gh_config_name)
				);
				console.log(`[Layout][${timestamp}] Matching config:`, matchingConfig);

				if (matchingConfig) {
					console.log(
						`[Layout][${timestamp}] Fetching entity for config code:`,
						matchingConfig.gh_config_code
					);
					const entityResponse = await getEntityBySecret(
						matchingConfig.gh_config_code
					);

					// Log original logo URLs
					console.log(`[Layout][${timestamp}] Original logo URLs:`, {
						small: entityResponse.entity?.me_logo_small,
						medium: entityResponse.entity?.me_logo_med,
					});

					// Prioritaskan me_logo_small untuk favicon dan hapus timestamps
					let logoUrl = entityResponse.entity?.me_logo_small || '/favicon.ico';

					// Hapus timestamps dari URL jika ada
					if (logoUrl && logoUrl.includes('?timestamps=')) {
						const originalUrl = logoUrl;
						logoUrl = logoUrl.split('?timestamps=')[0] || logoUrl;
						console.log(`[Layout][${timestamp}] Cleaned logo URL:`, {
							original: originalUrl,
							cleaned: logoUrl,
						});
					}

					// Test URL accessibility
					try {
						const testFetch = await fetch(logoUrl, { method: 'HEAD' });
						console.log(`[Layout][${timestamp}] Logo URL accessibility test:`, {
							url: logoUrl,
							status: testFetch.status,
							ok: testFetch.ok,
						});
					} catch (fetchError) {
						console.error(
							`[Layout][${timestamp}] Logo URL tidak dapat diakses:`,
							{
								url: logoUrl,
								error: (fetchError as Error).message,
							}
						);
						logoUrl = '/favicon.ico'; // Fallback ke favicon default jika URL tidak dapat diakses
					}

					setFaviconUrl(logoUrl);
					setPageTitle(matchingConfig.gh_config_data || 'HI-Guard');

					console.log(`[Layout][${timestamp}] Final favicon settings:`, {
						url: logoUrl,
						title: matchingConfig.gh_config_data || 'HI-Guard',
					});
				} else {
					console.warn(
						`[Layout][${timestamp}] Tidak ada config yang cocok untuk hostname:`,
						currentHost
					);
					setFaviconUrl('/favicon.ico');
				}
			} catch (error) {
				console.error(`[Layout][${timestamp}] Error dalam fetchEntityData:`, {
					message: (error as Error).message,
					stack: (error as Error).stack,
					response: (error as any).response?.data,
					status: (error as any).response?.status,
				});
				setFaviconUrl('/favicon.ico');
			}
		};

		fetchEntityData();
	}, []);

	// Tambahkan useEffect untuk memantau perubahan faviconUrl
	useEffect(() => {
		console.log('[Layout] Favicon URL updated:', {
			url: faviconUrl,
			timestamp: new Date().toISOString(),
		});
	}, [faviconUrl]);

	useEffect(() => {
		// Listener untuk event sessionExpired
		const handleSessionExpired = () => {
			setShowSessionExpiredModal(true);
			console.log('jalan ajar');
		};

		window.addEventListener('sessionExpired', handleSessionExpired);

		return () => {
			window.removeEventListener('sessionExpired', handleSessionExpired);
		};
	}, []);

	const handleModalClose = () => {
		setShowSessionExpiredModal(false);
		window.location.href = '/login';
	};

	return (
		<html lang={i18n.language}>
			<head>
				<meta name="viewport" content="width=device-width, initial-scale=1.0" />
				<meta name="description" content="Hi-Guard" />
				<meta name="keywords" content="Hi-Guard" />
				<meta name="author" content="HIK | Superapps" />
				{/* Favicon tags dengan spesifikasi yang lebih lengkap */}
				<link
					rel="icon"
					type="image/png"
					sizes="32x32"
					href={faviconUrl}
					key={`icon32-${faviconUrl}`}
					onError={(e) => {
						console.warn('[Layout] Error loading 32x32 favicon:', {
							url: faviconUrl,
							timestamp: new Date().toISOString(),
							error: e,
						});
						(e.target as HTMLLinkElement).href = '/favicon.ico';
					}}
				/>
				<link
					rel="icon"
					type="image/png"
					sizes="16x16"
					href={faviconUrl}
					key={`icon16-${faviconUrl}`}
					onError={(e) => {
						console.warn('[Layout] Error loading 16x16 favicon:', {
							url: faviconUrl,
							timestamp: new Date().toISOString(),
							error: e,
						});
						(e.target as HTMLLinkElement).href = '/favicon.ico';
					}}
				/>
				<link
					rel="shortcut icon"
					type="image/x-icon"
					href={faviconUrl}
					key={`shortcut-${faviconUrl}`}
					onError={(e) => {
						console.warn('[Layout] Error loading shortcut icon:', {
							url: faviconUrl,
							timestamp: new Date().toISOString(),
							error: e,
						});
						(e.target as HTMLLinkElement).href = '/favicon.ico';
					}}
				/>
				<link
					rel="apple-touch-icon"
					sizes="180x180"
					href={faviconUrl}
					key={`apple-${faviconUrl}`}
				/>

				{/* Tambahkan manifest untuk PWA support */}
				<link rel="manifest" href="/manifest.json" />
				<title>{pageTitle}</title>
			</head>
			<body className="bg-slate-100">
				<I18nextProvider i18n={i18n}>
					<QueryClientProvider client={queryClient}>
						<AttendanceFilterProvider>
							<EntityProvider>
								{/* Hanya load PermissionProvider jika bukan halaman login/public */}
								{!isLoginPage && !isLocketLayout && !isPrivacyPolicyPage && !isNotFoundPage ? (
									<PermissionProvider>
										{!isChangePasswordPage && (
											<Navbar
												changeLanguage={handleChangeLanguage}
												toggleSidebar={toggleSidebar}
											/>
										)}
										{!isChangePasswordPage && (
											<Sidebar isOpen={isSidebarOpen} language={languageProps} />
										)}

										<div className={`flex flex-col mt-20 p-2`}>
											<div
												className={`flex-grow transition-all duration-300 ease-in-out ${
													isSidebarOpen && !isChangePasswordPage
														? 'ml-56'
														: 'ml-0'
												}`}
											>
												{children}
											</div>
										</div>

										{/* Modal untuk session expired */}
										<Modal
											show={showSessionExpiredModal}
											onClose={handleModalClose}
										>
											<Modal.Header>Sesi Berakhir</Modal.Header>
											<Modal.Body>
												<p className="text-dark">
													Waktu sesi Anda telah habis. Anda akan dialihkan ke
													halaman login.
												</p>
											</Modal.Body>
											<Modal.Footer>
												<Button onClick={handleModalClose} color="failure">
													OK
												</Button>
											</Modal.Footer>
										</Modal>
									</PermissionProvider>
								) : (
									<>
										{children}
										{/* Modal untuk session expired di halaman public */}
										<Modal
											show={showSessionExpiredModal}
											onClose={handleModalClose}
										>
											<Modal.Header>Sesi Berakhir</Modal.Header>
											<Modal.Body>
												<p className="text-dark">
													Waktu sesi Anda telah habis. Anda akan dialihkan ke
													halaman login.
												</p>
											</Modal.Body>
											<Modal.Footer>
												<Button onClick={handleModalClose} color="failure">
													OK
												</Button>
											</Modal.Footer>
										</Modal>
									</>
								)}
							</EntityProvider>
						</AttendanceFilterProvider>
					</QueryClientProvider>
				</I18nextProvider>
			</body>
		</html>
	);
}
